/**
 * Vertical USP marquee: shows one USP at a time, cycles infinitely with seamless loop.
 * Clones items so the transition from last → first is invisible. Respects prefers-reduced-motion.
 */
class UspVerticalMarquee extends HTMLElement {
  /** @type {number | undefined} */
  #intervalId;

  connectedCallback() {
    const track = this.querySelector(".usp-marquee-vertical__track");
    if (!track) return;

    const items = Array.from(track.children);
    if (items.length <= 1) return;

    // Clone items so we can scroll to the duplicate and reset without a visible jump (infinite loop)
    const itemCount = items.length;
    items.forEach((el) => {
      track.appendChild(el.cloneNode(true));
    });

    const duration = Number(this.dataset.durationSeconds || 3) * 1000;
    const itemHeight = items[0].offsetHeight;
    track.style.setProperty("--usp-item-height", `${itemHeight}px`);

    let index = 0;
    let resetting = false;

    const resetToZero = () => {
      track.style.transition = "none";
      index = 0;
      track.style.setProperty("--usp-index", "0");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          track.style.transition = "";
          resetting = false;
        });
      });
    };

    const transitionEndHandler = (e) => {
      if (e.target !== track || e.propertyName !== "transform") return;
      track.removeEventListener("transitionend", transitionEndHandler);
      resetToZero();
    };

    const next = () => {
      if (resetting) return;
      if (index === itemCount) return; // at clone position, wait for transitionend to reset

      index += 1;
      track.style.setProperty("--usp-index", String(index));

      if (index === itemCount) {
        resetting = true;
        track.addEventListener("transitionend", transitionEndHandler);
      }
    };

    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      next();
      this.#intervalId = setInterval(next, duration);
    }
  }

  disconnectedCallback() {
    if (this.#intervalId !== undefined) {
      clearInterval(this.#intervalId);
      this.#intervalId = undefined;
    }
  }
}

if (!customElements.get("usp-vertical-marquee")) {
  customElements.define("usp-vertical-marquee", UspVerticalMarquee);
}

export default UspVerticalMarquee;
